import { type FileMessage } from '@sendbird/chat/message';

const DOCUMENT_VAULT_CHANNEL_DATA_KEY = 'documentVault';

interface DocumentVaultChannelData {
    fileDisplayNames?: Record<string, string>;
}

export const getOriginalFileName = (fileMessage: FileMessage): string =>
    fileMessage.name || 'Unknown file';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value != null && !Array.isArray(value);

const parseChannelData = (channelData: string): Record<string, unknown> => {
    if (channelData === '') {
        return {};
    }

    try {
        const parsed: unknown = JSON.parse(channelData);
        if (!isPlainObject(parsed)) {
            return {};
        }

        return parsed;
    } catch {
        return {};
    }
};

const isDocumentVaultChannelData = (
    value: unknown,
): value is DocumentVaultChannelData => {
    if (!isPlainObject(value)) {
        return false;
    }

    if (!('fileDisplayNames' in value)) {
        return true;
    }

    const { fileDisplayNames } = value;
    if (fileDisplayNames == null) {
        return true;
    }

    if (!isPlainObject(fileDisplayNames)) {
        return false;
    }

    return Object.values(fileDisplayNames).every(
        (displayName) => typeof displayName === 'string',
    );
};

export const getDocumentVaultDisplayNames = (
    channelData: string,
): Record<string, string> => {
    const parsed = parseChannelData(channelData);
    const documentVault = parsed[DOCUMENT_VAULT_CHANNEL_DATA_KEY];

    if (!isDocumentVaultChannelData(documentVault)) {
        return {};
    }

    return documentVault.fileDisplayNames ?? {};
};

export const getDocumentVaultFileDisplayName = (
    messageId: number,
    fileMessage: FileMessage,
    displayNames: Record<string, string>,
): string => {
    const originalName = getOriginalFileName(fileMessage);
    const customDisplayName = displayNames[String(messageId)]?.trim();

    if (customDisplayName == null || customDisplayName === '') {
        return originalName;
    }

    return customDisplayName;
};

export const hasCustomDocumentVaultDisplayName = (
    messageId: number,
    fileMessage: FileMessage,
    displayNames: Record<string, string>,
): boolean => {
    const originalName = getOriginalFileName(fileMessage);
    return (
        getDocumentVaultFileDisplayName(
            messageId,
            fileMessage,
            displayNames,
        ) !== originalName
    );
};

export const buildChannelDataWithDocumentVaultDisplayName = (
    existingChannelData: string,
    messageId: number,
    displayName: string,
): string => {
    const parsed = parseChannelData(existingChannelData);
    const existingDocumentVault = parsed[DOCUMENT_VAULT_CHANNEL_DATA_KEY];
    const documentVault: DocumentVaultChannelData = isDocumentVaultChannelData(
        existingDocumentVault,
    )
        ? { ...existingDocumentVault }
        : {};
    const fileDisplayNames = { ...documentVault.fileDisplayNames };
    const trimmedDisplayName = displayName.trim();
    const messageIdKey = String(messageId);

    if (trimmedDisplayName === '') {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- Delete entry if empty name.
        delete fileDisplayNames[messageIdKey];
    } else {
        fileDisplayNames[messageIdKey] = trimmedDisplayName;
    }

    if (Object.keys(fileDisplayNames).length === 0) {
        delete documentVault.fileDisplayNames;
    } else {
        documentVault.fileDisplayNames = fileDisplayNames;
    }

    if (Object.keys(documentVault).length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- If empty, remove documentVault from channel data.
        delete parsed[DOCUMENT_VAULT_CHANNEL_DATA_KEY];
    } else {
        parsed[DOCUMENT_VAULT_CHANNEL_DATA_KEY] = documentVault;
    }

    return Object.keys(parsed).length === 0 ? '' : JSON.stringify(parsed);
};

export const shouldPersistDocumentVaultDisplayName = (
    messageId: number,
    fileMessage: FileMessage,
    displayNames: Record<string, string>,
    nextDisplayName: string,
): boolean => {
    const trimmedNextDisplayName = nextDisplayName.trim();
    const originalName = getOriginalFileName(fileMessage);
    const currentDisplayName = getDocumentVaultFileDisplayName(
        messageId,
        fileMessage,
        displayNames,
    );

    if (trimmedNextDisplayName === '') {
        return hasCustomDocumentVaultDisplayName(
            messageId,
            fileMessage,
            displayNames,
        );
    }

    if (trimmedNextDisplayName === originalName) {
        return hasCustomDocumentVaultDisplayName(
            messageId,
            fileMessage,
            displayNames,
        );
    }

    return trimmedNextDisplayName !== currentDisplayName;
};
